/**
 * Reactive "are we at/above the Ukeplanlegger's desktop breakpoint" check —
 * used to gate whole-card pointer drag (Slice 4b) to desktop only.
 *
 * The breakpoint (1180px) is also where `.planner-desktop`/`.planner-mobile`
 * switch and where `.recipe-card-drag-handle`/`.recipe-card-quick-add` swap
 * in `app.css` — `PLANNER_DESKTOP_BREAKPOINT_PX` is the ONE place that number
 * lives in JS, so this hook and that CSS can't silently drift apart (the CSS
 * media queries carry a comment pointing back here).
 *
 * WHY this needs to be reactive (not a one-time `matchMedia().matches` read):
 * a card that's resized across the breakpoint (window resize, orientation
 * change, devtools) must pick up the new draggable/non-draggable state
 * immediately — `useSyncExternalStore` subscribes to the media query's
 * `change` event so React re-renders exactly when the breakpoint crosses.
 *
 * WHY this must be enforced at all (not just via the CSS hiding the grip):
 * once dnd-kit's pointer listeners live on the whole card (see `RecipeCard`),
 * a touch scroll starting on a card would otherwise be captured as a drag —
 * breaking normal vertical scrolling on mobile. `disabled: !isPlannerDesktop`
 * on `useDraggable` is the actual guard; this hook is what feeds it.
 */
import { useSyncExternalStore } from "react";

export const PLANNER_DESKTOP_BREAKPOINT_PX = 1180;

const QUERY = `(min-width: ${PLANNER_DESKTOP_BREAKPOINT_PX}px)`;

function subscribe(callback: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return () => undefined;
  }
  const mql = window.matchMedia(QUERY);
  // Safari < 14 only has the deprecated addListener/removeListener pair;
  // every environment we actually ship to (and jsdom, once mocked) supports
  // addEventListener, so that's the only path implemented here.
  mql.addEventListener("change", callback);
  return () => mql.removeEventListener("change", callback);
}

function getSnapshot(): boolean {
  // Guard for `matchMedia` being undefined (older jsdom / SSR-safety) by
  // treating it as "not desktop" — the safe default is to keep drag OFF.
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(QUERY).matches;
}

function getServerSnapshot(): boolean {
  return false;
}

/** True at/above the Ukeplanlegger's 1180px desktop breakpoint; reacts to viewport changes. */
export function usePlannerDesktop(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
